/**
 * React Query hooks for suggestions
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { generateSuggestions, bulkAcceptSuggestions, reparseSuggestions } from '@/api/suggestions';
import {
  SuggestionRequest,
  SuggestionResponse,
  BulkAcceptRequest,
  BulkAcceptResponse,
  ReparseSuggestionsRequest,
} from '@/types/api';
import { getErrorMessage } from '@/api/client';

/**
 * Hook to generate entry suggestions
 */
export const useGenerateSuggestions = () => {
  const queryClient = useQueryClient();

  return useMutation<SuggestionResponse, Error, SuggestionRequest>({
    mutationFn: generateSuggestions,
    onSuccess: () => {
      // Invalidate entries cache after generating suggestions
      queryClient.invalidateQueries({ queryKey: ['entries'] });
    },
    onError: (error) => {
      console.error('Failed to generate suggestions:', getErrorMessage(error));
    },
  });
};

/**
 * Hook to bulk accept suggestions
 */
export const useBulkAcceptSuggestions = () => {
  const queryClient = useQueryClient();

  return useMutation<BulkAcceptResponse, Error, BulkAcceptRequest>({
    mutationFn: bulkAcceptSuggestions,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entries'] });
    },
    onError: (error) => {
      console.error('Failed to bulk accept suggestions:', getErrorMessage(error));
    },
  });
};

/**
 * Hook to re-parse suggestions with a forced pattern
 */
export const useReparseSuggestions = () => {
  return useMutation<SuggestionResponse, Error, ReparseSuggestionsRequest>({
    mutationFn: reparseSuggestions,
    onError: (error) => {
      console.error('Failed to reparse suggestions:', getErrorMessage(error));
    },
  });
};

export default useGenerateSuggestions;
